import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Brain,
  Database,
  Globe,
  Scale,
  Server,
  Shield,
} from 'lucide-react';
import { useRevealOnScroll } from '../../../hooks/useRevealOnScroll';
import { PRODUCT_NAME_MARKETING } from '../../../constants';
import { T05ProductShell } from './T05ProductShell';
import { T05ReferoFeatureRow } from './T05ReferoFeatureRow';
import { T05HeroLeadForm } from '../../landing-templates/t05-launchdarkly/T05HeroLeadForm';
import { T05LeadModal } from '../../landing-templates/t05-launchdarkly/T05LeadModal';
import { useT05ProductLead } from '../../../lib/t05ProductLead';
import type { T05ProductPageProps } from './routes.fragment';
import './t05-product.css';

const LEXCORE_LEAD = { interest: 'LEXCORE & BRAIN – Demo', quelle: 't05-product-lexcore' };

interface ProductLexcoreProps extends T05ProductPageProps {}

const LEXCORE_POINTS = [
  {
    icon: Scale,
    title: 'Kuratierter Rechtskern',
    text: 'Deterministische Norm-Engine für österreichisches Recht — jede Antwort mit norm_id und RIS-Beleg, kein Halluzinations-Blackbox.',
  },
  {
    icon: Globe,
    title: 'RIS-Anbindung',
    text: 'Inhalte aus dem Rechtsinformationssystem (RIS): Gesetze, Verordnungen und Rechtsprechung — strukturiert, versioniert, nachvollziehbar.',
  },
  {
    icon: Shield,
    title: 'Kein CLOUD Act',
    text: 'Referenz-Stack in der EU (Frankfurt). Keine US-Cloud-Pflicht, keine Datenübermittlung an Drittländer für den Rechtskern.',
  },
];

const BRAIN_POINTS = [
  {
    icon: Brain,
    title: 'Kanzlei-eigenes Wissen',
    text: 'Embeddings und Vektorsuche in Qdrant — Ihre internen Mustertexte, Präzedenzfälle und Wissensbasen am Edge.',
  },
  {
    icon: Database,
    title: 'Semantische Suche',
    text: 'BRAIN ergänzt LEXCORE um kontextuelle Treffer aus Ihren Dokumenten — Mandantendaten bleiben auf Ihrem Server.',
  },
  {
    icon: Server,
    title: 'Edge-lokal',
    text: 'Ingest und Abfrage auf Ihrem Kunden-GEX44. PII verlässt den Edge nicht; LEXCORE wird lesend mit Tenant-Scope angesprochen.',
  },
];

const REFERENZ_ITEMS = [
  'LEXCORE — deterministische Norm-Engine',
  'BRAIN — Qdrant + Embeddings (ohne Mandanten-PII)',
  'RIS-Korpus, kuratiert & versioniert',
  'Zentral bei ARKORBIT (EU Frankfurt)',
];

const EDGE_ITEMS = [
  'Portal, ARKO, OnlyOffice, Nextcloud',
  'Ollama & Whisper lokal',
  'Mandanten-PII & Akten',
  'Dedizierter Kunden-GEX44 (Hetzner EU)',
];

function ReferenzEdgeDiagram() {
  return (
    <div className="t05-arch-diagram" aria-label="Referenz+Edge Architektur">
      <div className="t05-arch-diagram__node t05-arch-diagram__node--ref">
        <p className="t05-arch-diagram__label">Referenz (ARKORBIT EU)</p>
        <h3 className="t05-arch-diagram__title">LEXCORE + BRAIN</h3>
        <ul className="t05-arch-diagram__list">
          {REFERENZ_ITEMS.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
      <div className="t05-arch-diagram__arrow" aria-hidden>
        HTTPS · Tenant-Scope →
      </div>
      <div className="t05-arch-diagram__node t05-arch-diagram__node--edge">
        <p className="t05-arch-diagram__label">Edge (Ihre Kanzlei)</p>
        <h3 className="t05-arch-diagram__title">{PRODUCT_NAME_MARKETING}</h3>
        <ul className="t05-arch-diagram__list">
          {EDGE_ITEMS.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function LexcoreRisPanel() {
  return (
    <div className="t05-product-panel" aria-label="LEXCORE RIS-Kern">
      <div className="t05-product-panel__bar" aria-hidden>
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
      </div>
      <p className="t05-product-panel__title">lexcore://ris-kern</p>
      <div className="t05-product-panel__grid">
        {[
          { label: 'MRG § 23 — Kündigung', badge: 'RIS' },
          { label: 'ABGB § 1295', badge: 'Kuratiert' },
          { label: 'OGH 4Ob123/24y', badge: 'Quelle' },
          { label: 'norm_id + Beleg', badge: 'Deterministisch' },
        ].map((row) => (
          <div key={row.label} className="t05-product-panel__row">
            <span>{row.label}</span>
            <span className="t05-product-panel__badge">{row.badge}</span>
            <span className="t05-product-panel__status" aria-hidden />
          </div>
        ))}
      </div>
    </div>
  );
}

function BrainQdrantPanel() {
  return (
    <div className="t05-product-panel" aria-label="BRAIN Vektorsuche">
      <div className="t05-product-panel__bar" aria-hidden>
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
      </div>
      <p className="t05-product-panel__title">brain://qdrant/edge</p>
      <div className="t05-product-panel__grid">
        {[
          { label: 'Mustertexte Kanzlei', badge: 'Embedding' },
          { label: 'Präzedenzfälle intern', badge: 'Vektor' },
          { label: 'Mandant PII', badge: 'Edge only' },
          { label: 'LEXCORE lesend', badge: 'Tenant-Scope' },
        ].map((row) => (
          <div key={row.label} className="t05-product-panel__row">
            <span>{row.label}</span>
            <span className="t05-product-panel__badge">{row.badge}</span>
            <span className="t05-product-panel__status" aria-hidden />
          </div>
        ))}
      </div>
    </div>
  );
}

function EuCompliancePanel() {
  return (
    <div className="t05-prod-screen" aria-label="EU-Compliance — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">lexcore://compliance/eu</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-prod-screen__row">
          <span>Hosting Frankfurt (Hetzner EU)</span>
          <span className="t05-prod-screen__badge">DSGVO</span>
        </div>
        <div className="t05-prod-screen__row">
          <span>Kein CLOUD Act · Keine US-Subunternehmer-Pflicht</span>
          <span className="t05-prod-screen__badge">EU</span>
        </div>
        <div className="t05-prod-screen__row">
          <span>§ 9 RAO · norm_id + RIS-Verweis</span>
          <span className="t05-product-panel__badge">Beleg</span>
        </div>
      </div>
    </div>
  );
}

export const ProductLexcore: React.FC<ProductLexcoreProps> = ({ onLoginClick, onLeadSubmit }) => {
  useRevealOnScroll();

  const lead = useT05ProductLead({ onLeadSubmit }, LEXCORE_LEAD);
  const openDemo = () => lead.openDemo(LEXCORE_LEAD.interest);

  return (
    <T05ProductShell
      productLabel="LEXCORE & BRAIN"
      onLoginClick={onLoginClick}
      onDemoClick={openDemo}
      navAnchors={[
        { href: '#architektur', label: 'Architektur' },
        { href: '#rollen', label: 'LEXCORE vs. BRAIN' },
      ]}
    >
      <header className="t05-hero t05-hero--product t05-hero--compact">
        <p className="t05-eyebrow reveal-up">Rechtskern · Referenz+Edge</p>
        <h1 className="t05-headline t05-headline--product reveal-up">
          LEXCORE &amp; BRAIN
          <span className="t05-headline__accent"> — quellenbelegt.</span>
        </h1>
        <p className="t05-lead reveal-up">
          <strong>LEXCORE</strong> liefert den kuratierten österreichischen Rechtskern mit RIS-Beleg.
          <strong> BRAIN</strong> ergänzt Ihr kanzlei-eigenes Wissen — Referenz zentral in der EU, Mandantendaten am Edge.
          Kein CLOUD Act, keine US-Cloud-Pflicht.
        </p>
        <T05HeroLeadForm lead={lead} />
      </header>

      <section className="t05-container t05-refero-features" aria-label="LEXCORE Funktionen">
        <T05ReferoFeatureRow
          id="architektur"
          accent="voltage"
          label="Architektur"
          labelIcon={Server}
          title="Referenz+Edge — zwei Ebenen, eine Antwort"
          description="Referenz+Edge-Architektur: LEXCORE und BRAIN bleiben zentral bei ARKORBIT — ohne Mandanten-PII. Ihr Kanzlei-Stack am Edge verarbeitet Akten und ruft den Rechtskern lesend per HTTPS ab."
          bullets={[
            { icon: Server, text: 'Referenz zentral in der EU (Frankfurt) — LEXCORE + BRAIN ohne Mandanten-PII' },
            { icon: Shield, text: 'Edge auf Ihrem Kunden-GEX44 — Akten, PII und lokale KI' },
            { icon: Globe, text: 'HTTPS mit Tenant-Scope — lesender Zugriff auf den Rechtskern' },
          ]}
          visual={<ReferenzEdgeDiagram />}
        />

        <T05ReferoFeatureRow
          id="rollen"
          accent="gold"
          label="LEXCORE"
          labelIcon={Scale}
          title={
            <>
              Deterministisch · <em>RIS</em>
            </>
          }
          description="LEXCORE beantwortet „Was sagt das Gesetz?“ — jede Norm mit norm_id und RIS-Beleg, kein Halluzinations-Blackbox."
          bullets={LEXCORE_POINTS.map((item) => ({
            icon: item.icon,
            text: `${item.title}: ${item.text}`,
          }))}
          visual={<LexcoreRisPanel />}
        />

        <T05ReferoFeatureRow
          reverse
          accent="trust"
          label="BRAIN"
          labelIcon={Brain}
          title={
            <>
              Semantisch · <em>Edge</em>
            </>
          }
          description="BRAIN beantwortet „Was wissen wir intern dazu?“ — ARKO kombiniert beides mit quellenbelegten Antworten."
          bullets={BRAIN_POINTS.map((item) => ({
            icon: item.icon,
            text: `${item.title}: ${item.text}`,
          }))}
          footer={
            <Link to="/produkt/arko" className="t05-stack-item__cta">
              ARKO ansehen <ArrowRight size={16} aria-hidden />
            </Link>
          }
          visual={<BrainQdrantPanel />}
        />

        <T05ReferoFeatureRow
          accent="coral"
          label="EU & Nachvollziehbarkeit"
          labelIcon={BookOpen}
          title="Hosting, Compliance und Belege"
          description="Referenz-Infrastruktur und Edge-Server in der EU — DSGVO-konform, ohne US-Subunternehmer-Zwang für den Rechtskern. Jede Norm-Antwort mit Beleg."
          bullets={[
            {
              icon: BookOpen,
              text: 'EU-Hosting: Referenz-Infrastruktur in Frankfurt (Hetzner). Edge-Server ebenfalls EU — DSGVO-konform.',
            },
            {
              icon: Shield,
              text: 'Kein CLOUD Act: Mandantendaten und Akten verbleiben auf Ihrem Edge. LEXCORE/BRAIN enthalten keine Mandanten-PII.',
            },
            {
              icon: Scale,
              text: '§ 9 RAO & Nachvollziehbarkeit: Jede Norm-Antwort mit Beleg (norm_id, RIS-Verweis). Keine Blackbox.',
            },
          ]}
          visual={<EuCompliancePanel />}
        />
      </section>

      <section className="t05-final-cta t05-container">
        <div className="t05-final-cta__band reveal-up">
          <div className="t05-final-cta__inner">
            <h2 className="t05-title">LEXCORE in ARKO erleben</h2>
            <p className="t05-subtitle">
              Quellenbelegte Antworten aus RIS-Kern und kanzlei-eigenem Wissen — auf Ihrem Edge-Server.
            </p>
            <div className="t05-cta-actions">
              <button type="button" className="t05-btn t05-btn--primary" onClick={openDemo}>
                Demo anfragen
              </button>
              <Link to="/produkt/arko" className="t05-btn t05-btn--ghost">
                ARKO ansehen <ArrowRight size={16} aria-hidden />
              </Link>
              <Link to="/" className="t05-btn t05-btn--ghost">
                Zur Startseite
              </Link>
            </div>
          </div>
        </div>
      </section>
      <T05LeadModal lead={lead} />
    </T05ProductShell>
  );
};

export default ProductLexcore;
